import { useState, useEffect } from 'react';

interface MetricsData {
  response_time: number;
  response_time_display: string;
  resolution_rate: number;
  resolution_rate_display: string;
  customers_served: number;
  customer_satisfaction: number;
  active_tickets: number;
  customer_retention?: number;
  customer_retention_display?: string;
  active_clients?: number;
  active_clients_display?: string;
}

interface UseMetricsReturn {
  metrics: MetricsData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch and manage metrics data across components
 */
export const useMetrics = (): UseMetricsReturn => {
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        // First try static cached metrics for immediate display
        try {
          const staticResponse = await fetch('/initial-metrics.json');
          if (staticResponse.ok) {
            const staticData = await staticResponse.json();
            if (staticData.success && staticData.data) {
              setMetrics({
                response_time: staticData.data.response_time,
                response_time_display: staticData.data.response_time_display || `${staticData.data.response_time}min`,
                resolution_rate: staticData.data.resolution_rate,
                resolution_rate_display: staticData.data.resolution_rate_display || `${staticData.data.resolution_rate}%`,
                customers_served: staticData.data.customers_served || 25,
                customer_satisfaction: staticData.data.customer_satisfaction || 4.8,
                active_tickets: staticData.data.active_tickets || 0,
                customer_retention: staticData.data.customer_retention || 81,
                customer_retention_display: staticData.data.customer_retention_display || '81%',
                active_clients: staticData.data.active_clients || 25,
                active_clients_display: staticData.data.active_clients_display || '25+'
              });
            }
          }
        } catch (staticError) {
          console.log('Static metrics not available, will use API only');
        }

        // Then fetch live metrics (may update values if newer data available)
        const liveResponse = await fetch('/api/metrics');
        if (!liveResponse.ok) {
          throw new Error(`API Error: ${liveResponse.status}`);
        }

        const liveData = await liveResponse.json();
        if (liveData.success && liveData.data) {
          // Round customers_served down to nearest 5 for consistency
          const customersServed = liveData.data.customers_served || 25;
          const roundedCustomers = Math.floor(customersServed / 5) * 5;
          
          setMetrics({
            response_time: liveData.data.response_time,
            response_time_display: liveData.data.response_time_display || `${liveData.data.response_time}min`,
            resolution_rate: liveData.data.resolution_rate,
            resolution_rate_display: liveData.data.resolution_rate_display || `${liveData.data.resolution_rate}%`,
            customers_served: roundedCustomers,
            customer_satisfaction: liveData.data.customer_satisfaction || 4.8,
            active_tickets: liveData.data.active_tickets || 0,
            customer_retention: liveData.data.customer_retention,
            customer_retention_display: liveData.data.customer_retention_display,
            active_clients: liveData.data.active_clients,
            active_clients_display: liveData.data.active_clients_display
          });
        }

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
        console.error('Error fetching metrics:', err);
        
        // Provide fallback metrics on error
        if (!metrics) {
          setMetrics({
            response_time: 15,
            response_time_display: '15min',
            resolution_rate: 90,
            resolution_rate_display: '90%',
            customers_served: 25,
            customer_satisfaction: 4.8,
            active_tickets: 0,
            customer_retention: 81,
            customer_retention_display: '81%',
            active_clients: 25,
            active_clients_display: '25+'
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  return { metrics, loading, error };
};